import { RadarSliceTooltipProps } from '@nivo/radar';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { getTestCountsByFocusArea } from 'libs/end-to-end/src/tests-utils';
import React, { useContext, useMemo } from 'react';
import { DefaultPolarChartContext } from 'apps/sigma2-ui/src/components/PolarChart';
import { DefaultPolarDataPoint } from 'apps/sigma2-ui/src/components/PolarChart';
import { PolarChart } from 'apps/sigma2-ui/src/components/PolarChart';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';

const OverviewPolarChartTooltip = ({ index }: RadarSliceTooltipProps) => {
  const label = index;
  const context = useContext(DefaultPolarChartContext);
  const language = useLanguage();

  // Pulls in PolarDataPoint[] from context
  const dataPoint = context.dataPoints.find((x) => x.label === label);

  if (!dataPoint) {
    return null;
  }
  const calibrationLevel = getCalibrationLevel(dataPoint.calibrationScore ?? 0);

  const testStatement = language.polarChartTooltipTestStatement({
    passingTests: dataPoint.passingTests ?? 0,
    totalTests: dataPoint.totalTests ?? 0,
  });

  return (
    <div
      className="filter shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md"
      data-qa={`tooltip${label}`}
    >
      <p
        className="text-xl font-bold font-sans"
        data-qa={`tooltip${label}Title`}
      >
        {label}
      </p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        data-qa={`tooltip${label}Pill`}
        calibrationFraming={context.calibrationFraming}
      />
      <p data-qa={`tooltip${label}TestStatement`}>{testStatement}</p>
    </div>
  );
};

export type OverviewFocusAreaPolarChartProps = {
  testSummary: TestSummary | null;
  focusAreas: MyFocusAreaDefinition[] | null;
  calibrationFraming: CalibrationFraming;
};

export const OverviewFocusAreaPolarChart: React.FC<
  OverviewFocusAreaPolarChartProps
> = ({ testSummary, focusAreas, calibrationFraming }) => {
  const dataPoints: DefaultPolarDataPoint[] = useMemo(() => {
    const dataPoints: DefaultPolarDataPoint[] = [];

    if (!focusAreas || !testSummary) {
      return dataPoints;
    }

    for (const focusArea of focusAreas) {
      const counts = getTestCountsByFocusArea(
        testSummary.tests,
        focusArea.focusArea,
      );

      if (!counts) {
        dataPoints.push({
          label: focusArea.displayName,
          calibrationScore: 0,
          passingTests: 0,
          incompleteTests: 0,
          totalTests: 0,
        });

        continue;
      }

      const calibrationScore = getCalibrationScore({
        testCount: counts,
      });

      dataPoints.push({
        label: focusArea.displayName,
        calibrationScore,
        passingTests: counts.passing,
        incompleteTests: counts.incomplete,
        totalTests: getTotalTestCount(counts),
      });
    }
    return dataPoints;
  }, [focusAreas, testSummary]);

  return (
    <PolarChart
      dataPoints={dataPoints}
      variant={'default'}
      tooltip={OverviewPolarChartTooltip}
      calibrationFraming={calibrationFraming}
      dataTestId="overviewFocusAreaPolarChart"
    />
  );
};
